import { useEditorRuntimeStore } from '../stores/useEditorRuntimeStore';
import { useEditorStore, type ElementMap } from '../stores/useEditorStore';

import { getElementBounds, isElementInViewport } from './bound';
import { getConfig } from './config';
import { type GenericElement, type ElementCanvas } from './element';
import { getPlugin } from './plugin';

export const canvasCache = new Map<
  string,
  { canvas: ElementCanvas; version: string }
>();

export type PathCache = Map<string, { path: Path2D; version: string }>;
export const pathsCache: PathCache = new Map();

export type OverwriteState = {
  zoom?: number;
  offsetX?: number;
  offsetY?: number;
  skipElementUpdate?: boolean;
  isExportImage?: boolean;
};

export const cappedElementCanvasSize = (
  element: GenericElement,
  padding: number,
  overwriteState?: OverwriteState,
): { height: number; scale: number; width: number } => {
  const config = getConfig();
  const { zoom } = useEditorRuntimeStore.getState();

  let width = element.width + padding * 2;
  let height = element.height + padding * 2;

  let scale: number = overwriteState?.zoom ?? zoom.value;

  if (
    width * scale > config.canvas.widthHeightLimit ||
    height * scale > config.canvas.widthHeightLimit
  ) {
    scale = Math.min(
      config.canvas.widthHeightLimit / width,
      config.canvas.widthHeightLimit / height,
    );
  }

  if (width * height * scale * scale > config.canvas.areaLimit) {
    scale = Math.sqrt(config.canvas.areaLimit / (width * height));
  }

  width = Math.floor(width * scale);
  height = Math.floor(height * scale);

  return { height, scale, width };
};

export const generateElementCanvas = (
  element: GenericElement,
  overwriteState?: OverwriteState,
): ElementCanvas | undefined => {
  const config = getConfig();
  const plugin = getPlugin(element.type);
  const runtimeState = useEditorRuntimeStore.getState();
  const elementState = useEditorStore.getState();

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d')!;
  const padding = plugin.render.padding?.(element) ?? 0;

  const { height, scale, width } = cappedElementCanvasSize(
    element,
    padding,
    overwriteState,
  );

  if (!width || !height) return;

  canvas.width = width;
  canvas.height = height;

  let canvasOffsetX = 0;
  let canvasOffsetY = 0;

  const translatePayload = plugin.render.translate?.(context, element, scale);

  if (translatePayload) {
    canvasOffsetX = translatePayload.x;
    canvasOffsetY = translatePayload.y;
  }

  context.save();
  context.translate(padding * scale, padding * scale);
  context.scale(scale, scale);

  plugin.render.draw({
    cache: pathsCache,
    config,
    context,
    element,
    elementState,
    runtimeState: {
      ...runtimeState,
      ...(overwriteState?.offsetX !== undefined && {
        scrollX: overwriteState.offsetX,
      }),
      ...(overwriteState?.offsetY !== undefined && {
        scrollY: overwriteState.offsetY,
      }),
      ...(overwriteState?.zoom !== undefined && {
        zoom: { value: overwriteState.zoom },
      }),
    },
    skipElementUpdate: overwriteState?.skipElementUpdate,
    isExportImage: overwriteState?.isExportImage,
  });

  context.restore();

  return {
    canvas,
    canvasOffsetX,
    canvasOffsetY,
    padding,
    scale,
    zoom:
      overwriteState?.zoom !== undefined
        ? { value: overwriteState.zoom }
        : runtimeState.zoom,
  };
};

export const generateElementWithCanvas = (
  element: GenericElement,
  forceRerender?: boolean,
) => {
  const state = useEditorRuntimeStore.getState();

  const prevCanvas = canvasCache.get(element.id);

  const shouldRegenerateBecauseZoom =
    prevCanvas &&
    prevCanvas.canvas.zoom.value !== state.zoom.value &&
    !state.shouldCacheIgnoreZoom;

  if (
    !prevCanvas ||
    prevCanvas.version !== element.version.canvas ||
    shouldRegenerateBecauseZoom ||
    forceRerender
  ) {
    const elementCanvas = generateElementCanvas(element);
    if (!elementCanvas) return;
    canvasCache.set(element.id, {
      canvas: elementCanvas,
      version: element.version.canvas,
    });
    return elementCanvas;
  }

  return prevCanvas.canvas;
};

export const drawElementFromCanvas = (
  element: GenericElement,
  canvasElement: ElementCanvas,
  context: CanvasRenderingContext2D,
  overwriteState?: OverwriteState,
) => {
  if (element.renderedById) return;

  const state = useEditorRuntimeStore.getState();

  const [x1, y1, x2, y2] = getElementBounds(element, { withRotation: false });
  const cx = (x1 + x2) / 2 + (overwriteState?.offsetX ?? state.scrollX);
  const cy = (y1 + y2) / 2 + (overwriteState?.offsetY ?? state.scrollY);

  context.save();
  context.scale(1, 1);

  context.translate(cx, cy);

  const plugin = getPlugin(element.type);

  if (!plugin.transformation.manualRotation) {
    context.rotate(element.angle);
  }
  if (element.scale) {
    context.scale(element.scale[0], element.scale[1]);
  }

  context.translate(-cx, -cy);

  context.drawImage(
    canvasElement.canvas,
    x1 +
      (overwriteState?.offsetX ?? state.scrollX) -
      (canvasElement.padding * canvasElement.scale) / canvasElement.scale,
    y1 +
      (overwriteState?.offsetY ?? state.scrollY) -
      (canvasElement.padding * canvasElement.scale) / canvasElement.scale,
    canvasElement.canvas.width / canvasElement.scale,
    canvasElement.canvas.height / canvasElement.scale,
  );

  context.restore();
};

export const getVisibleElements = (elements: ElementMap) => {
  const visibleElements: GenericElement[] = [];

  for (const element of Array.from(elements.values())) {
    if (
      isElementInViewport(element, window.innerWidth, window.innerHeight) &&
      element.isVisible
    ) {
      visibleElements.push(element);
    }
  }

  return visibleElements;
};

export const renderElement = (
  element: GenericElement,
  context: CanvasRenderingContext2D,
  forceRerender?: boolean,
  overwriteState?: OverwriteState,
) => {
  const canvas = overwriteState
    ? generateElementCanvas(element, overwriteState)
    : generateElementWithCanvas(element, forceRerender);

  if (!canvas) return;
  drawElementFromCanvas(element, canvas, context, overwriteState);
};
